import { cx } from '@flexbyt/widget-preview';
import type { Crumb } from '../../lib/seo.js';
import { SiteLink } from '../ui/button.js';

/**
 * The trail, drawn.
 *
 * The counterpart to `breadcrumbSchema` — and both are needed, because they
 * answer different readers. The JSON-LD is how the trail appears under a search
 * result; this is how somebody who *arrived* on a deep page from that result
 * works out where they are and what else is here. A page that emits the schema
 * without drawing the trail is describing navigation it does not have.
 *
 * The last crumb is the current page, so it is text rather than a link —
 * a link to where you already are is a link that does nothing, and screen
 * readers announce it as a destination.
 */
export function Breadcrumbs({
  trail,
  className,
}: {
  trail: Crumb[];
  className?: string;
}) {
  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-ink-500">
        {trail.map((crumb, index) => {
          const last = index === trail.length - 1;
          return (
            <li key={crumb.path} className="flex items-center gap-2">
              {index > 0 ? (
                <span aria-hidden className="text-ink-300">
                  /
                </span>
              ) : null}

              {last ? (
                <span aria-current="page" className="text-ink-700">
                  {crumb.name}
                </span>
              ) : (
                <SiteLink
                  to={crumb.path}
                  className={cx(
                    'rounded-sm underline-offset-[3px]',
                    'transition-colors duration-[var(--duration-fast)]',
                    'hover:text-ink-950 hover:underline'
                  )}
                >
                  {crumb.name}
                </SiteLink>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
